import type { IAlertsRepository } from '@/alerts/domain/repositories/alerts.repository';
import type { ResponseOf } from '@/api/fe-api';
import {
  alertsV1FindingsThroughputPerDayRetrieve,
  alertsV1ListRetrieve,
} from '@/api/fe-api/__generated__/oazapfts-client';
import { appConfig } from '@/config';
import { type Alert, AlertSchema } from '@/entities/alert';
import { AlertLevel } from '@/entities/alert-level';
import { AlertStatus } from '@/entities/alert-status';
import { AlertType, alertTypeToApi } from '@/entities/alert-type';
import type { Auth } from '@/entities/auth';
import type { Me } from '@/entities/me';
import type { FeatureFlagChecker } from '@/hooks/use-feature-flag';
import type { RequestOptions } from '@/pcs/domain/repositories/utils/request-options';
import { isWarrantyEnabled } from '@/utils/migration/warranty-version';

import { taskMapper } from '../../mappers/task.mapper';

// -----------------------------------------------------------------------------
// Helper mappings between API enums and internal enums
// -----------------------------------------------------------------------------

type AlertStatusEnum = ResponseOf<
  typeof alertsV1ListRetrieve
>['alerts'][number]['alert_status'];

// API type alias – create separate type name to avoid eslint complaining
type AlertTypeEnum = ResponseOf<
  typeof alertsV1ListRetrieve
>['alerts'][number]['alert_type'];

// API Level enum (excluding empty string)
type LevelEnum = Exclude<
  ResponseOf<typeof alertsV1ListRetrieve>['alerts'][number]['level'],
  ''
>;

const alertLevelFromApi: Record<LevelEnum, AlertLevel> = {
  system: AlertLevel.SYSTEM,
  pcs: AlertLevel.PCS,
  array: AlertLevel.ARRAY,
  node: AlertLevel.NODE,
  container: AlertLevel.CONTAINER,
  bank: AlertLevel.BANK,
  pack: AlertLevel.PACK,
  module: AlertLevel.MODULE,
  cell: AlertLevel.CELL,
};

const alertTypeFromApi: Record<AlertTypeEnum, AlertType> = {
  safety: AlertType.SAFETY,
  imbalance: AlertType.IMBALANCE,
  soc_offset: AlertType.SOC_OFFSET,
  performance: AlertType.PERFORMANCE,
  inactivity: AlertType.INACTIVITY,
  power_availability: AlertType.PCS_POWER_AVAILABILITY,
  pcs_efficiency: AlertType.PCS_EFFICIENCY,
  power_capability: AlertType.PCS_POWER_CAPABILITY,
  warranty_condition: AlertType.WARRANTY_CONDITION,
  warranty_penalty: AlertType.WARRANTY_PENALTY,
  warranty_tag: AlertType.WARRANTY_TAG,
  warranty_tag_pcs: AlertType.WARRANTY_TAG_PCS,
  warranty_rte: AlertType.WARRANTY_RTE,
  warranty_soh: AlertType.WARRANTY_SOH,
  no_finding: AlertType.NO_FINDING,
};

const alertStatusFromApi: Record<AlertStatusEnum, AlertStatus> = {
  Critical: AlertStatus.CRITICAL,
  Warning: AlertStatus.WARNING,
  Info: AlertStatus.INFO,
  OK: AlertStatus.OK,
  Tripped: AlertStatus.TRIPPED,
};

// -----------------------------------------------------------------------------
// Mock alerts used for warranty v3 demo customer (id = 49)
// -----------------------------------------------------------------------------

function getMockAlertsV2Data(
  filters: IAlertsRepository.GetAllInput['filters'],
): Alert[] {
  const now = new Date();
  const mockAlerts: Alert[] = [
    {
      id: '2',
      fullFrontendId: 'Smart_Hill/B05/Rack_03',
      level: AlertLevel.PACK,
      type: AlertType.WARRANTY_PENALTY,
      status: AlertStatus.WARNING,
      suggestedAction:
        'Adjust operation to avoid collecting additional penalty points',
      changedAt: now,
      systemFrontendId: 'Smart_Hill',
    },
  ];

  return mockAlerts.filter((alert) => {
    if (
      filters.alertStatusList.length > 0 &&
      !filters.alertStatusList.includes(alert.status)
    ) {
      return false;
    }

    if (
      filters.alertTypeList.length > 0 &&
      !filters.alertTypeList.includes(alert.type)
    ) {
      return false;
    }

    const daysSinceChanged =
      (now.getTime() - alert.changedAt.getTime()) / (1000 * 60 * 60 * 24);
    if (daysSinceChanged > filters.changedSinceDays) {
      return false;
    }

    if (
      filters.groupIds.length > 0 &&
      (alert.systemFrontendId === undefined ||
        !filters.groupIds.includes(alert.systemFrontendId))
    ) {
      return false;
    }

    return true;
  });
}

// -----------------------------------------------------------------------------
// Mock alerts used for warranty v3 demo customer (id = 49)
// -----------------------------------------------------------------------------

function getMockAlertsV3Data(
  filters: IAlertsRepository.GetAllInput['filters'],
): Alert[] {
  const now = new Date();
  const mockAlerts: Alert[] = [
    {
      id: '34567',
      fullFrontendId: 'Smart_Hill',
      level: AlertLevel.SYSTEM,
      type: AlertType.WARRANTY_TAG,
      status: AlertStatus.WARNING,
      suggestedAction: 'Check the warranty metric',
      changedAt: now,
      systemFrontendId: 'Smart_Hill',
    },
    {
      id: '34567',
      fullFrontendId: 'Smart_Hill',
      level: AlertLevel.SYSTEM,
      type: AlertType.WARRANTY_RTE,
      status: AlertStatus.WARNING,
      suggestedAction: 'Check the warranty metric',
      changedAt: now,
      systemFrontendId: 'Smart_Hill',
    },
    {
      id: '34567',
      fullFrontendId: 'Smart_Hill',
      level: AlertLevel.SYSTEM,
      type: AlertType.WARRANTY_SOH,
      status: AlertStatus.WARNING,
      suggestedAction: 'Check the warranty metric',
      changedAt: now,
      systemFrontendId: 'Smart_Hill',
    },
    {
      id: '34567',
      fullFrontendId: 'Smart_Hill',
      level: AlertLevel.SYSTEM,
      type: AlertType.WARRANTY_PENALTY,
      status: AlertStatus.WARNING,
      suggestedAction:
        'Adjust operation to avoid collecting additional penalty points',
      changedAt: now,
      systemFrontendId: 'Smart_Hill',
    },
  ];

  return mockAlerts.filter((alert) => {
    if (
      filters.alertStatusList.length > 0 &&
      !filters.alertStatusList.includes(alert.status)
    ) {
      return false;
    }

    if (
      filters.alertTypeList.length > 0 &&
      !filters.alertTypeList.includes(alert.type)
    ) {
      return false;
    }

    const daysSinceChanged =
      (now.getTime() - alert.changedAt.getTime()) / (1000 * 60 * 60 * 24);
    if (daysSinceChanged > filters.changedSinceDays) {
      return false;
    }

    if (
      filters.groupIds.length > 0 &&
      (alert.systemFrontendId === undefined ||
        !filters.groupIds.includes(alert.systemFrontendId))
    ) {
      return false;
    }

    return true;
  });
}

// -----------------------------------------------------------------------------
// Repository implementation
// -----------------------------------------------------------------------------

export class ApiAlertsRepository implements IAlertsRepository {
  constructor(
    private readonly auth: Auth,
    private readonly me: Me,
    private readonly featureFlag: FeatureFlagChecker,
    private readonly baseUrl: string = appConfig.bffUrl,
  ) {}

  readonly key = 'api-alerts';

  private shouldIncludeWarrantyAlerts(): boolean {
    return (
      this.featureFlag('warrantyAlerts') &&
      isWarrantyEnabled(this.me.config.featureFlags, this.me.isInternal)
    );
  }

  async getAll(
    { filters, includeTask }: IAlertsRepository.GetAllInput,
    requestOptions?: RequestOptions,
  ): Promise<IAlertsRepository.GetAllOutput> {
    const accessToken = await this.auth.getAccessToken();

    const { alertStatusList, changedSinceDays, groupIds, alertTypeList } =
      filters;

    // Filter out unsupported alert types before sending to API
    const supportedAlertTypes = alertTypeList?.filter(
      (type) => alertTypeToApi[type],
    ) as AlertTypeEnum[] | undefined;

    const response = await alertsV1ListRetrieve(
      {
        alertStatusList,
        alertTypes: supportedAlertTypes,
        changedSinceDays,
        frontendGroupIds: groupIds,
        customerType: this.me.config.type,
        includePcsAlerts: this.featureFlag('pcs'),
        includeWarrantyAlerts: this.shouldIncludeWarrantyAlerts(),
        includeTask,
      },
      {
        baseUrl: `${this.baseUrl}/plex`,
        headers: {
          authorization: `Bearer ${accessToken}`,
          'x-user-email': this.me.email,
          'x-user-name': this.me.displayName,
        },
        signal: requestOptions?.signal,
      },
    );

    if (response.status !== 200) {
      throw new Error('Failed to fetch alerts');
    }

    let apiAlerts = response.data.alerts.map<Alert>((alert) => ({
      id: alert.accure_id,
      fullFrontendId: alert.full_frontend_id,
      level:
        alert.level !== '' ? alertLevelFromApi[alert.level] : AlertLevel.SYSTEM,
      type: alertTypeFromApi[alert.alert_type],
      status: alertStatusFromApi[alert.alert_status],
      suggestedAction: alert.suggested_action,
      changedAt: new Date(alert.status_changed_date),
      systemFrontendId: alert.system_frontend_id,
      task: alert.task ? taskMapper(alert.task) : undefined,
    }));

    if (
      this.featureFlag('warranty') &&
      this.featureFlag('warrantyV3') &&
      this.me.config.id === '49'
    ) {
      const mockAlerts = getMockAlertsV3Data(filters);
      apiAlerts = [...apiAlerts, ...mockAlerts];
    } else if (
      this.featureFlag('warranty') &&
      this.featureFlag('warrantyV2') &&
      this.me.config.id === '49'
    ) {
      const mockAlerts = getMockAlertsV2Data(filters);
      apiAlerts = [...apiAlerts, ...mockAlerts];
    }

    const alerts = AlertSchema.array().parse(apiAlerts);

    // Sort by status priority then by fullFrontendId
    const statusPriority: Record<AlertStatus, number> = {
      [AlertStatus.CRITICAL]: 1,
      [AlertStatus.TRIPPED]: 2,
      [AlertStatus.WARNING]: 3,
      [AlertStatus.INFO]: 4,
      [AlertStatus.OK]: 5,
    };

    alerts.sort((a, b) => {
      const prioDiff = statusPriority[a.status] - statusPriority[b.status];
      if (prioDiff !== 0) return prioDiff;
      return a.fullFrontendId.localeCompare(b.fullFrontendId);
    });

    return alerts;
  }

  async getFindingsThroughput(
    { daysRange }: IAlertsRepository.GetFindingsThroughputInput,
    requestOptions?: RequestOptions,
  ): Promise<IAlertsRepository.GetFindingsThroughputOutput> {
    const accessToken = await this.auth.getAccessToken();

    const response = await alertsV1FindingsThroughputPerDayRetrieve(
      {
        daysRange,
        includePcsAlerts: this.featureFlag('pcs'),
        includeWarrantyAlerts: this.shouldIncludeWarrantyAlerts(),
      },
      {
        baseUrl: `${this.baseUrl}/plex`,
        headers: {
          authorization: `Bearer ${accessToken}`,
          'x-user-email': this.me.email,
          'x-user-name': this.me.displayName,
        },
        signal: requestOptions?.signal,
      },
    );

    if (response.status !== 200) {
      throw new Error('Failed to fetch findings throughput');
    }

    return response.data.chart_data.map((point) => ({
      date: new Date(point.date),
      critical: point.critical,
      warning: point.warning,
      throughput: point.throughput,
    }));
  }
}
